import './LoginModal.scss';

import Loading, { LoadingBall } from '@ingka/loading';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Actions } from '../../../actions/enums';
import { PREFIX } from '../../Skapa/constants/constants';
import Modal, { ModalBody, ModalHeader, SheetSize, Sheets } from '../../Skapa/Modal/Modal';
import { splitPath } from '@mvecom/common/utils/splitPath';

import { useLogin } from '../../../hooks/useLogin';
import { getBaseUrl } from '../../../utils/url';
import { Login } from '../../Login/LoginPage';

export const LoginModal = (props: {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  loginInfo: ReturnType<typeof useLogin>;
}) => {
  const { open, setOpen } = props;
  const [menuIsLoading, setMenuIsLoading] = useState(true);
  const close = useCallback(() => {
    setTimeout(() => setMenuIsLoading(true), 500);
    setOpen(false);
  }, [setOpen]);
  const { loggedIn, login, loginError, loadingLoggedIn, loginLoading } = props.loginInfo;

  const iframeUrl = useMemo(() => {
    const href = window.location.href;
    const url = new URL(getBaseUrl(href) + '/profile/menu/');
    url.searchParams.set('openedBy', window.location.href);

    return url.toString();
  }, []);

  // listen to pubsub messages for opening the menu
  useEffect(() => {
    const open = () => {
      if (!onProfilePages()) {
        /*
          We can handle menu better on profile pages
          faster loading, avoid whole navigation when clicking on links
        */
        setOpen(true);
      }
    };

    window?.ikea?.pubsub?.subscribe(Actions.PROFILE_OPEN_MENU, open);

    return () => {
      window?.ikea?.pubsub?.unsubscribe(Actions.PROFILE_OPEN_MENU, open);
    };
  }, [loggedIn, setOpen]);

  // listen to post messages
  useEffect(() => {
    const ev = (event: MessageEvent) => {
      const pubsubStart = 'pubsub::';

      if (event.data === 'menu-loaded') {
        setMenuIsLoading(false);
      } else if (event.data === 'menu-close') {
        close();
      } else if (typeof event.data === 'string' && event.data.startsWith(pubsubStart)) {
        window?.ikea?.pubsub?.publish?.(event.data.slice(pubsubStart.length));
      }
    };
    window.addEventListener('message', ev);
    return () => window.removeEventListener('message', ev);
  }, [close]);

  return (
    <Modal prefix={PREFIX} id="menuModal" visible={open} handleCloseBtn={close}>
      <Sheets
        prefix={PREFIX}
        id="menuSheetsModal"
        header={<ModalHeader />}
        size={SheetSize.SMALL}
        footer={null}
        preserveAlignment
      >
        <ModalBody prefix={PREFIX} className="skapasModalBody">
          <div className="menuModalBodyContainer">
            {(loadingLoggedIn || (menuIsLoading && loggedIn)) && (
              <Loading prefix={PREFIX} text="">
                <LoadingBall prefix={PREFIX} />
              </Loading>
            )}

            {!loadingLoggedIn &&
              (loggedIn ? (
                <iframe
                  src={iframeUrl}
                  className="profilePagesIframe"
                  sandbox="allow-top-navigation allow-same-origin allow-scripts allow-forms"
                />
              ) : (
                <Login
                  closeMenu={close}
                  skapaPrefix={PREFIX}
                  login={login}
                  loginLoading={loginLoading}
                  loginError={loginError}
                />
              ))}
          </div>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

function onProfilePages() {
  const [_countryCode, _languageCode, appName] = splitPath(window.location.pathname);
  return appName === 'profile';
}
